import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-14">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-question-line text-2xl text-foreground-400" />
            </div>
            <h1 className="text-xl font-bold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-foreground-500 text-sm mb-6">该文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex px-6 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id)
    .slice(0, 3);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.publishDate,
    dateModified: article.publishDate,
    image: article.image,
    publisher: { '@type': 'Organization', name: 'JMComic', url: 'https://drawgates.github.io' },
    url: `https://drawgates.github.io/article/${article.id}`,
    mainEntityOfPage: { '@type': 'WebPage', '@id': `https://drawgates.github.io/article/${article.id}` },
    about: article.tags.map((tag) => ({ '@type': 'Thing', name: tag })),
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://drawgates.github.io' },
      { '@type': 'ListItem', position: 2, name: '文章', item: 'https://drawgates.github.io/category/all' },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      <ScrollToTop />
      <SEO
        title={`${article.title} – JMComic 官方入口与使用指南`}
        description={article.description}
        keywords={`jmcomic,${article.tags.join(',')},漫画阅读,jmcomic使用教程`}
        canonicalUrl={`https://drawgates.github.io/article/${article.id}`}
        ogImage={article.image}
        ogType="article"
        articleData={{
          publishedTime: article.publishDate,
          author: article.author,
          tags: article.tags,
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Article Header */}
          <article className="pt-20 pb-14">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <div className="mb-6">
                <Breadcrumb
                  items={[
                    { label: '首页', to: '/' },
                    { label: article.tags[0] },
                    { label: article.title },
                  ]}
                />
              </div>

              {/* Article Image */}
              <div className="rounded-xl overflow-hidden mb-8 aspect-[16/9]">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              {/* Meta */}
              <div className="flex flex-wrap items-center gap-3 mb-4">
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs bg-accent-100 text-accent-700 px-2.5 py-1 rounded-full font-medium">
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 font-[var(--font-heading)] mb-3 leading-tight">
                {article.title}
              </h1>

              <p className="text-foreground-600 text-sm md:text-base mb-6 leading-relaxed">
                {article.description}
              </p>

              <div className="flex items-center gap-4 text-xs text-foreground-500 pb-6 border-b border-background-200/60">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line" /> {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line" /> {article.publishDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line" /> {article.readTime}
                </span>
              </div>

              {/* Article Content */}
              <div
                className="prose-custom mt-8 text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </div>
          </article>

          {/* Related Articles */}
          <section className="py-14 bg-background-100 px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-[var(--font-heading)] mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all duration-200"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-3 md:p-4">
                      <h3 className="text-sm font-medium text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2 mb-1">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-500">{ra.readTime}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}